<template>
  <PhoneInput
    class="flex"
    countryLocale="en-EN"
    :ignored-countries="['AC']"
    noUseBrowserLocale
    fetchCountry
    @update="onUpdate"
  >
    <template #selector="{ inputValue, updateInputValue, countries }">
      <Popover v-model:open="isOpen">
        <PopoverTrigger>
          <Button
            type="button"
            variant="outline"
            :class="[
              'flex h-10 w-full justify-start gap-1 rounded-md rounded-e-none rounded-s-lg border border-r-0 border-input/20 bg-background/0 px-3 py-1 text-left text-sm font-normal shadow-sm ring-0 transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium hover:bg-background/0 hover:text-white/60 focus:border-input/100 focus:outline-none focus:ring-0 focus:ring-transparent disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive',
            ]"
            :disabled="disabled"
            @click.prevent
          >
            <FlagComponent :country="inputValue" />
            <ChevronsUpDown class="-mr-2 h-4 w-4 fill-white stroke-white opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" class="w-[300px] rounded-md p-0 shadow-sm transition-colors">
          <Command class="w-[300px] rounded-md border border-input/20">
            <CommandInput
              placeholder="Search country..."
              class="mt-1 ring-0 focus:border-input/100 focus:outline-none focus:ring-0 focus:ring-transparent"
            />
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandList>
              <CommandGroup>
                <CommandItem
                  v-for="option in countries"
                  :key="option.iso2"
                  :value="option.name"
                  class="gap-2"
                  @select="
                    () => {
                      updateInputValue(option.iso2);
                      isOpen = false;
                      focused = true;
                    }
                  "
                >
                  <FlagComponent :country="option?.iso2" />
                  <span class="flex-1 text-sm">{{ option.name }}</span>
                  <span class="text-sm text-foreground/50">{{ option.dialCode }}</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </template>

    <template #input="{ updateInputValue, placeholder }">
      <Input
        ref="phoneInput"
        type="text"
        class="border-collapse rounded-s-none"
        :model-value="formattedValue"
        :placeholder="placeholder"
        :error="error"
        :disabled="disabled"
        autocomplete="tel"
        @input="updateInputValue"
      />
    </template>
  </PhoneInput>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useFocus } from '@vueuse/core';
import { ChevronsUpDown } from 'lucide-vue-next';
import PhoneInput from 'base-vue-phone-input';
import FlagComponent from './FlagComponent.vue';
import Button from '@ui/button/Button.vue';
import Popover from '@ui/popover/Popover.vue';
import PopoverTrigger from '@ui/popover/PopoverTrigger.vue';
import PopoverContent from '@ui/popover/PopoverContent.vue';
import Command from '@ui/command/Command.vue';
import CommandInput from '@ui/command/CommandInput.vue';
import CommandEmpty from '@ui/command/CommandEmpty.vue';
import CommandList from '@ui/command/CommandList.vue';
import CommandGroup from '@ui/command/CommandGroup.vue';
import CommandItem from '@ui/command/CommandItem.vue';
import Input from '@ui/input/Input.vue';

const isOpen = ref(false);
const phoneInput = ref(null);
const { focused } = useFocus(phoneInput);
const formattedValue = ref('');

withDefaults(
  defineProps<{
    error?: string;
    disabled?: boolean;
  }>(),
  {
    error: undefined,
    disabled: false,
  },
);

const onUpdate = (value: { isValid: boolean; formatInternational: string }) => {
  formattedValue.value = value.formatInternational;
};
</script>
